// Three-Point Estimate (PERT) calculator: React UI on the BoldPanelLayout template.
// The maths lives in ./model.js; the page holds the task list and projects
// analysePert(). It hands its total to the Sprint Forecast as remaining work.

import { useMemo, useState } from "react";
import { BoldPanelLayout } from "@/components/templates";
import {
  BarFooter,
  BriefLines,
  Callout,
  DataTable,
  ExampleDataPicker,
  Explainer,
  Field,
  FieldGrid,
  Graph,
  MetricCard,
  MetricGrid,
  OpenInTool,
  Panel,
  PillToggle,
  UrlStateNotices,
  useUrlRestore,
  useUrlSync,
} from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { headingFor, ledeOf } from "@/lib/pageHeadings";
import { hubLink, hubState, initialBack } from "@/lib/handBack.js";
import { TOGETHER, UNITS, WIDEN, analysePert, fmt, pct, summaryLines } from "./model.js";
import { EXAMPLES, exampleById } from "./examples.js";
import { pertShare } from "./share.js";
import { DEFAULT_STATE as SPRINTS_DEFAULT, sprintsShare } from "@/sprints/share.js";

const HEADING = headingFor("tool/pert-calculator");

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

interface Task {
  label: string;
  low: number;
  likely: number;
  high: number;
}
interface State {
  unit: string;
  widen: string;
  together: string;
  target: number | null;
  tasks: Task[];
}

const INITIAL: State = EXAMPLES[0].state as State;
const num = (text: string) => (text.trim() === "" ? NaN : Number(text));
const show = (x: number | null) => (x != null && Number.isFinite(x) ? String(x) : "");

export function App() {
  const shared = useUrlRestore<State>(pertShare, (s) => setState(s));
  const [state, setState] = useState<State>(shared.initial ?? INITIAL);
  const [back] = useState(() => initialBack(["sprints"]));
  useUrlSync(pertShare, () => state, [state], shared);

  const r: Any = useMemo(() => analysePert(state), [state]);
  const set = (patch: Partial<State>) => setState((s) => ({ ...s, ...patch }));
  const setTask = (i: number, patch: Partial<Task>) =>
    setState((s) => ({ ...s, tasks: s.tasks.map((t, j) => (j === i ? { ...t, ...patch } : t)) }));
  const u = state.unit;

  const sprintUnit = u === "points" || u === "days" ? u : null;
  const forecastHref = (() => {
    if (r.empty || !sprintUnit) return undefined;
    const hub = (hubState(back, sprintsShare) as Any) ?? SPRINTS_DEFAULT;
    return hubLink("/tool/sprint-forecast", sprintsShare, {
      ...hub,
      unit: sprintUnit,
      remaining: { low: Math.round(r.p5 * 10) / 10, high: Math.round(r.p95 * 10) / 10 },
    });
  })();

  return (
    <BoldPanelLayout
      label="Unit 35 / Three-point estimate"
      tags={<span className="hidden md:inline">Local · nothing leaves the page</span>}
      eyebrow="PERT calculator"
      title={HEADING.h1}
      lede={ledeOf(HEADING)}
      footer={
        <BarFooter>
          <span>Beta-PERT per task · summed by simulation</span>
          <span>{r.empty ? "Add a task" : `${state.tasks.length} tasks · 85% within ${fmt(r.p85)} ${u}`}</span>
        </BarFooter>
      }
    >
      <Explainer id="pert" title="What this answers" summary="How long a list of tasks takes, as a chance" defaultOpen>
        <p>
          Most plans add up the <strong>most likely</strong> value of each task. Because tasks overrun more often
          and by more than they finish early, that sum is usually beaten less than half the time.
        </p>
        <p>
          This page draws every task from its range thousands of times and adds them up. It reports the total
          you finish within 50%, 85% and 95% of the time. Two settings say how far to trust the ranges: whether
          they are usually too narrow, and whether tasks run late together.
        </p>
      </Explainer>

      <Panel step={1} title="Start from an example" hint="Or enter your own tasks in 02">
        <UrlStateNotices state={shared} />
        <ExampleDataPicker
          examples={EXAMPLES.map(({ id, name, description }) => ({ id, name, description }))}
          onLoad={(id) => {
            const ex = exampleById(id);
            if (ex) setState(ex.state as State);
          }}
          deepLink={!shared.initial}
        />
        <p className="mb-0 mt-3 text-xs text-muted-foreground">Examples are illustrative: invented tasks and numbers.</p>
      </Panel>

      <Panel step={2} title="Tasks" hint="Optimistic ≤ most likely ≤ pessimistic">
        <FieldGrid className="mb-4">
          <Field label="Unit">
            <Select aria-label="Unit" value={u} onChange={(e) => set({ unit: e.target.value })}>
              {UNITS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Select>
          </Field>
        </FieldGrid>
        <DataTable<Task>
          rows={state.tasks}
          rowKey={(_, i) => i}
          rowClassName={(_, i) => (r.excluded?.some((e: Any) => e.index === i) ? "bg-destructive/10" : undefined)}
          columns={[
            {
              head: "Task",
              cell: (t, i) => (
                <Input aria-label={`Task ${i + 1} name`} className="h-8 rounded-none" value={t.label} onChange={(e) => setTask(i, { label: e.target.value })} />
              ),
            },
            ...(["low", "likely", "high"] as const).map((k) => ({
              head: k === "low" ? "Optimistic" : k === "likely" ? "Most likely" : "Pessimistic",
              numeric: true,
              cell: (t: Task, i: number) => (
                <Input
                  type="number"
                  min={0}
                  step="any"
                  aria-label={`Task ${i + 1} ${k === "low" ? "optimistic" : k === "likely" ? "most likely" : "pessimistic"}`}
                  className="h-8 w-24 rounded-none font-mono text-xs"
                  value={show(t[k])}
                  onChange={(e) => setTask(i, { [k]: num(e.target.value) } as Partial<Task>)}
                />
              ),
            })),
            {
              head: <span className="sr-only">Remove</span>,
              cell: (t, i) => (
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Remove ${t.label || `task ${i + 1}`}`}
                  disabled={state.tasks.length <= 1}
                  onClick={() => setState((s) => ({ ...s, tasks: s.tasks.filter((_, j) => j !== i) }))}
                >
                  ×
                </Button>
              ),
            },
          ]}
        />
        <Button
          className="mt-3"
          onClick={() => setState((s) => ({ ...s, tasks: [...s.tasks, { label: `Task ${s.tasks.length + 1}`, low: 1, likely: 2, high: 4 }] }))}
        >
          + Add task
        </Button>
        {r.excluded?.length > 0 && (
          <Callout kind="warn" className="mt-4">
            <strong>Not counted:</strong>
            <ul className="mb-0 mt-1 pl-5">
              {r.excluded.map((e: Any) => (
                <li key={e.index}>
                  {e.label || `Task ${e.index + 1}`}: {e.reasons.join(" ")}
                </li>
              ))}
            </ul>
          </Callout>
        )}
      </Panel>

      <Panel step={3} title="How far to trust the ranges" hint="Most ranges are too narrow">
        <Field label="Widen every range">
          <PillToggle aria-label="Widen every range" value={state.widen} onChange={(v) => set({ widen: v })} options={WIDEN.map((w) => ({ value: w.id, label: w.label }))} />
        </Field>
        <p className="mb-4 mt-2 text-sm text-muted-foreground">
          Teams usually finish outside their own ranges more often than they expect. If yours do, widen them: the
          most likely value stays, the ends move out.
        </p>
        <Field label="Tasks run late together">
          <PillToggle aria-label="Tasks run late together" value={state.together} onChange={(v) => set({ together: v })} options={TOGETHER.map((t) => ({ value: t.id, label: t.label }))} />
        </Field>
        <p className="mb-0 mt-2 text-sm text-muted-foreground">
          Tasks share people, code and surprises. When one is late, others often are too. This keeps the average
          total and makes the long end longer.
        </p>
      </Panel>

      <Panel step={4} title="Total" hint={r.empty ? "Add a usable task" : "Conditional on the task list being complete"}>
        {r.empty ? (
          <Callout kind="error">There is no usable task to add up.</Callout>
        ) : (
          <>
            <MetricGrid className="lg:grid-cols-3">
              <MetricCard eyebrow="Even odds" value={`${fmt(r.p50)} ${u}`}>
                Half the time the work takes longer than this.
              </MetricCard>
              <MetricCard accent eyebrow="85% of the time within" value={`${fmt(r.p85)} ${u}`} data-value={r.p85}>
                A commitment you keep about 6 times in 7.
              </MetricCard>
              <MetricCard eyebrow="95% of the time within" value={`${fmt(r.p95)} ${u}`}>
                Only 1 time in 20 does it take longer.
              </MetricCard>
              <MetricCard eyebrow="Sum of most likely values" value={`${fmt(r.sumLikely)} ${u}`}>
                Finished within this {pct(r.chanceWithinLikely)} of the time.
              </MetricCard>
              <MetricCard eyebrow="Average total" value={`${fmt(r.mean)} ${u}`}>
                Pulled up by the long pessimistic tails.
              </MetricCard>
              <MetricCard eyebrow="Your target" value={r.target == null ? "—" : pct(r.chanceWithinTarget)}>
                <label className="flex items-center gap-2">
                  <span>Finish within</span>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    aria-label="Target total"
                    className="h-8 w-24 rounded-none font-mono text-xs text-foreground"
                    value={show(state.target)}
                    onChange={(e) => set({ target: Number.isFinite(num(e.target.value)) ? num(e.target.value) : null })}
                  />
                  <span>{u}</span>
                </label>
              </MetricCard>
            </MetricGrid>
            <Graph
              className="mt-6"
              title="Chance of finishing within each total"
              caption={`Chance the whole list is done within each total, from ${(20000).toLocaleString("en-US")} simulated plans. Conditional on the task list being complete and the settings in 03.`}
              series={[{ id: "cdf", label: "Chance done within", points: r.curve, kind: "area", tone: "accent", width: 2.5 }]}
              markers={[
                { x: r.sumLikely, label: "Sum of most likely", tone: "ink", dashed: true },
                ...(r.target != null ? [{ x: r.target, label: "Target", tone: "muted" as const }] : []),
              ]}
              yDomain={[0, 1]}
              showY
              formatY={(v) => `${Math.round(v * 100)}%`}
              formatX={(v) => fmt(v)}
              xLabel={`Total (${u})`}
            />
            <BriefLines className="mt-6" lines={summaryLines(r, u)} />
            <Callout kind="note" className="mt-6">
              <strong>Classic PERT by formula:</strong> mean {fmt(r.classic.mean)} {u}, sd {fmt(r.classic.sd)}, 85% at{" "}
              {fmt(r.classic.p85)} and 95% at {fmt(r.classic.p95)} {u}. The formula assumes independent tasks and a normal
              total; the simulation above does not need either.
            </Callout>
          </>
        )}
      </Panel>

      {!r.empty && (
        <OpenInTool
          title="Use this total"
          targets={[
            {
              id: "sprints",
              name: "Sprint Forecast",
              use: back
                ? "Return to your sprint forecast with the 90% range of this total as the remaining work."
                : "Forecast how many sprints this work needs, with the 90% range of this total as the remaining work.",
              href: forecastHref,
              reason: "The Sprint Forecast counts work in points or days. Switch the unit to one of those.",
            },
          ]}
          note="Only the total range and its unit travel. Task names stay on this page."
        />
      )}
    </BoldPanelLayout>
  );
}
